/**
 * RendererAdapter
 * ----------------
 * Primary orchestration layer for the calendar rendering system.
 *
 * RESPONSIBILITIES
 * ---------------
 * - Owns renderer lifecycle (initialize → render → refresh → destroy)
 * - Coordinates state transitions via AdapterStateManager
 * - Delegates recovery/escalation decisions to AdapterErrorManager
 * - Manages runtime resources (calendar instance, renderer, plugins)
 *
 * ERROR HANDLING PHILOSOPHY
 * ------------------------
 * This adapter intentionally separates **telemetry** from **control flow**:
 *
 * 1. ErrorHandler (telemetry-only)
 *    - Records errors & successes
 *    - Maintains bounded in-memory history
 *    - Outputs to console
 *    - NEVER used to make lifecycle or state decisions
 *
 * 2. AdapterErrorManager (control & recovery)
 *    - Determines recoverability
 *    - Coordinates state transitions on error
 *    - Acts as the authority for adapter health
 *
 * RULE:
 * -----
 * RendererAdapter may WRITE to ErrorHandler, but must NEVER READ from it
 * to influence behavior.
 */

import { AdapterStateManager as StateManager, AdapterState } from '../utils/AdapterStateManager.js';
import AdapterErrorManager from '../utils/AdapterErrorManager.js';
import { RendererDependencyLoader } from '../utils/RendererDependencyLoader.js';
import ResourceManager from '../utils/ResourceManager.js';
import ErrorHandler from '../../utils/ErrorHandler.js';

/**
 * Telemetry-only error handler.
 * This instance is intentionally NOT injected into control logic.
 */
const telemetry = new ErrorHandler({
  module: 'RenderAdapter',
  maxErrors: 10,
});

/**
 * Lightweight constructor guard
 */
function assertRequired(name, value) {
  if (!value) throw new Error(`RendererAdapter: ${name} parameter is required`);
}

class RendererAdapter {
  constructor(config, logger, errorHandlerInstance, eventDataManager, dependencies = {}) {
    assertRequired('config', config);
    assertRequired('logger', logger);
    assertRequired('errorHandlerInstance', errorHandlerInstance);
    assertRequired('eventDataManager', eventDataManager);

    this.config = config;
    this.logger = logger;
    this.eventDataManager = eventDataManager;

    // Lifecycle & error authority
    this.stateManager = new StateManager(logger);
    this.errorManager = new AdapterErrorManager(errorHandlerInstance, this.stateManager);

    // Runtime resources
    this.dependencyLoader = new RendererDependencyLoader(dependencies);
    this.resourceManager = new ResourceManager();
  }

  /* ------------------------------------------------------------------
   * State Delegates
   * ------------------------------------------------------------------ */

  getState() {
    return this.stateManager.getCurrentState();
  }

  isInState(state) {
    return this.stateManager.isInState(state);
  }

  isReady() {
    return this.stateManager.isReady();
  }

  isOperational() {
    return this.stateManager.isOperational();
  }

  /* ------------------------------------------------------------------
   * Telemetry Helpers (write-only)
   * ------------------------------------------------------------------ */

  _logSuccess(message, metadata = {}) {
    telemetry.logSuccess(message, metadata);
  }

  _logError(message, error, metadata = {}) {
    telemetry.logError(message, error, metadata);
  }

  /* ------------------------------------------------------------------
   * Lifecycle
   * ------------------------------------------------------------------ */

  async initialize() {
    if (this.isOperational()) {
      this.logger.info('Calendar Renderer already initialized');
      return true;
    }

    try {
      this.stateManager.transitionTo(AdapterState.INITIALIZING);
      this.logger.info('🎨 Initializing Calendar Renderer...');

      const calendarEl = this._getCalendarElement();
      const eventsForFC = this.eventDataManager.transformForFullCalendar();

      const plugins = await this.dependencyLoader.getPlugins();
      const CalendarRenderer = await this.dependencyLoader.getCalendarRenderer();

      const { calendarRenderer, calendarInstance } =
        await this._withVisibleCalendarSection(calendarEl, async () => {
          const renderer = new CalendarRenderer(calendarEl, eventsForFC, { plugins });
          const instance = await renderer.createCalendar();
          return { calendarRenderer: renderer, calendarInstance: instance };
        });

      if (!calendarInstance) {
        const err = new Error('Failed to create calendar instance');
        this._logError('Initialization failed: calendar instance not created', err, {
          category: 'initialization',
        });
        throw err;
      }

      this.resourceManager.setCalendarRenderer(calendarRenderer);
      this.resourceManager.setCalendarInstance(calendarInstance);

      this.stateManager.transitionTo(AdapterState.INITIALIZED, {
        eventsCount: eventsForFC.length,
      });

      this._logSuccess('RenderAdapter initialized successfully', {
        category: 'initialization',
        eventsCount: eventsForFC.length,
      });

      this.logger.info('✅ Calendar Renderer initialized successfully');
      return true;
    } catch (err) {
      this._logError('Critical error during initialization', err, {
        category: 'initialization',
        state: this.getState(),
      });

      this.errorManager.handleError('Calendar Renderer initialization failed', err, 'initialization');
      return false;
    }
  }

  async render() {
    try {
      this._assertCanRender();

      if (this.getState() === AdapterState.INITIALIZED) {
        this.stateManager.transitionTo(AdapterState.READY);
      }

      this.stateManager.transitionTo(AdapterState.RENDERING);
      this.logger.info('🎨 Rendering calendar...');

      const renderer = this.resourceManager.getCalendarRenderer();
      if (!renderer) throw new Error('Calendar renderer unavailable');

      if (!renderer.render()) {
        throw new Error('renderer.render() returned false');
      }

      this.stateManager.transitionTo(AdapterState.READY);
      this.errorManager.clearError();

      this._logSuccess('Calendar rendered successfully', { category: 'render' });
      return true;
    } catch (err) {
      this._logError('Render operation failed', err, {
        category: 'render',
        state: this.getState(),
      });

      this.errorManager.handleError('Calendar render failed', err, 'render');
      return false;
    }
  }

  async refresh() {
    try {
      this._assertCanRefresh();

      const wasReady = this.isInState(AdapterState.READY);
      if (wasReady) this.stateManager.transitionTo(AdapterState.REFRESHING);

      const eventsForFC = this.eventDataManager.transformForFullCalendar();
      this._updateCalendarEvents(eventsForFC);

      if (wasReady) {
        this.stateManager.transitionTo(AdapterState.READY, {
          eventsCount: eventsForFC.length,
        });
      }

      this._logSuccess('Calendar refreshed successfully', {
        category: 'refresh',
        eventsCount: eventsForFC.length,
      });

      return true;
    } catch (err) {
      this._logError('Refresh operation failed', err, { category: 'refresh' });
      this.errorManager.handleError('Calendar refresh failed', err, 'render');
      return false;
    }
  }

  async destroy() {
    try {
      this.stateManager.forceState(AdapterState.DESTROYING);

      await this.resourceManager.cleanup();
      this.dependencyLoader.clearCache();
      this.errorManager.clearError();

      this.stateManager.transitionTo(AdapterState.DESTROYED);

      this._logSuccess('Renderer destroyed successfully', { category: 'lifecycle' });
    } catch (err) {
      this._logError('Destroy operation failed', err, { category: 'lifecycle' });
      this.errorManager.handleError('Failed to destroy renderer', err, 'cleanup');
      this.stateManager.forceState(AdapterState.DESTROYED);
    }
  }

  /* ------------------------------------------------------------------
   * Instance / Data
   * ------------------------------------------------------------------ */

  getCalendarInstance() {
    const instance = this.resourceManager.getCalendarInstance();

    if (!instance && this.isOperational()) {
      const err = new Error(`Calendar instance missing in state ${this.getState()}`);
      this._logError('Calendar instance missing', err, { category: 'configuration' });
      this.errorManager.handleError('Missing calendar instance', err, 'state');
    }

    return instance;
  }

  updateEvents(newEvents) {
    try {
      if (!Array.isArray(newEvents)) return false;

      const renderer = this.resourceManager.getCalendarRenderer();
      if (!renderer) return false;

      const events = this._transformRawEventsToFullCalendar(newEvents);
      return renderer.updateEvents(events);
    } catch (err) {
      this._logError('Event update failed', err, { category: 'events' });
      return false;
    }
  }

  getStatus() {
    return {
      state: this.getState(),
      isReady: this.isReady(),
      isOperational: this.isOperational(),
      hasInstance: this.resourceManager.hasCalendarInstance(),
      hasRenderer: this.resourceManager.hasCalendarRenderer(),
      eventsCount: this.eventDataManager?.getAllEvents?.()?.length || 0,
      lastError: this.errorManager.getLastError(),
      stateHistory: this.stateManager.getStateHistory(),
    };
  }

  /* ------------------------------------------------------------------
   * Debug / Telemetry Accessors (read-only)
   * ------------------------------------------------------------------ */

  getRecentErrors(limit = 5) {
    return telemetry.getRecentErrors(limit);
  }

  clearErrorHistory() {
    telemetry.clearErrorHistory();
  }

  /* ------------------------------------------------------------------
   * Guards
   * ------------------------------------------------------------------ */

  _assertCanRender() {
    const state = this.getState();

    if (state === AdapterState.DESTROYED) {
      throw new Error('Cannot render after destruction');
    }

    if (state === AdapterState.ERROR) {
      throw new Error('Cannot render while in error state');
    }
  }

  _assertCanRefresh() {
    if (!this.resourceManager.hasCalendarInstance()) {
      throw new Error('Cannot refresh without calendar instance');
    }
  }

  /* ------------------------------------------------------------------
   * Private Helpers
   * ------------------------------------------------------------------ */

  _getCalendarElement() {
    const el = document.querySelector(this.config.selectors.calendar);
    if (!el) throw new Error(`Calendar element not found`);
    return el;
  }

  async _withVisibleCalendarSection(calendarEl, fn) {
    const section = document.querySelector(this.config.selectors.calendarSection);
    const wasHidden = section?.classList.contains('hide');

    if (wasHidden) {
      section.classList.remove('hide');
      void calendarEl.offsetHeight; // force reflow
    }

    try {
      return await fn();
    } finally {
      if (wasHidden) section.classList.add('hide');
    }
  }

  _updateCalendarEvents(eventsForFC) {
    const instance = this.resourceManager.getCalendarInstance();
    instance.removeAllEvents();
    instance.addEventSource(eventsForFC);
  }

  _transformRawEventsToFullCalendar(rawEvents) {
    return rawEvents
      .map((e, i) => {
        const start = e.start?.dateTime || e.start?.date;
        if (!start) return null;

        return {
          id: e.id || `event-${i}`,
          title: e.summary || e.title || 'Untitled Event',
          start,
          end: e.end?.dateTime || e.end?.date || undefined,
          allDay: !!e.start?.date,
          extendedProps: { originalEvent: e },
        };
      })
      .filter(Boolean);
  }
}

export default RendererAdapter;
